import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Order, Orders, Summary } from "../pages/SalesPage/SalesPageInterface";
import { getOrdersByUser } from "../services/orders/orderAxios";
import { FilterOrdersType } from "../components/buttons/ButtonFilterTypes";
import { LogisticState } from "../types/order/logisticState";
import { PaidState } from "../types/order/paidState";
import { formatDateToISOString } from "../utils/formatters";
import { QueryFiltersParams } from "../services/orders/orderAxios";
import { Product } from "../components/products/ProductInterface";
import { getProducts } from "../services/products/productAxios";
import { handleApi } from "../utils/handleApi";
import { ConfirmationInfo, getConfirmationStatuses } from "../services/whatsapp/whatsappService";
import { decodeTokenService } from "../services/decodeTokenService";


export const useSalesData = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Orders>();
  const [filteredOrders, setFilteredOrders] = useState<Orders>();
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterOrdersType>("all");
  const [activeDateFilter, setActiveDateFilter] = useState<{
    label: string;
    startDate: Date;
    endDate: Date;
  } | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeProductFilters, setActiveProductFilters] = useState<{
    id: string;
    name: string;
  }[]>([]);
  const [confirmationStatuses, setConfirmationStatuses] = useState<Record<string, ConfirmationInfo>>({});
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);

  const getQueryFiltersFromURL = (): QueryFiltersParams => {
    const page = Number(searchParams.get('page') || '0');
    const size = Number(searchParams.get('size') || '15');

    return {
      date_init: searchParams.get('date_init') || null,
      date_end: searchParams.get('date_end') || null,
      product_ids: searchParams.get('product_ids')?.split(',').filter(Boolean) || [],
      page: Number.isFinite(page) && page >= 0 ? page : 0,
      size: Number.isFinite(size) && size > 0 ? size : 15,
    };
  };

  const loadProducts = async () => {
    const { res, error } = await handleApi(getProducts());
    if (error) return;
    setProducts(res || []);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const dateInit = searchParams.get('date_init');
    const dateEnd = searchParams.get('date_end');
    const filterLabel = searchParams.get('filter_label');
    
    if (dateInit && dateEnd) {
      setActiveDateFilter({
        label: filterLabel || `${dateInit} - ${dateEnd}`,
        startDate: new Date(dateInit),
        endDate: new Date(dateEnd),
      });
    }
    
    const productIds = searchParams.get('product_ids')?.split(',').filter(Boolean) || [];
    const productNames = searchParams.get('product_names')?.split('||').filter(Boolean) || [];
    
    if (productIds.length > 0 && productNames.length > 0) {
      const activeProducts = productIds.map((id, index) => ({
        id,
        name: productNames[index] || id,
      }));
      setActiveProductFilters(activeProducts);
    }
  }, []);

  useEffect(() => {
    const filters = getQueryFiltersFromURL();
    loadOrders(filters);
  }, [searchParams]);

  const applyFilter = (ordersList: Order[], filter: FilterOrdersType) => {
    switch (filter) {
      case "unfulfilled":
        return ordersList.filter((o) => o.logistic_state === LogisticState.CREATED);
      case "fulfilled":
        return ordersList.filter((o) => o.logistic_state === LogisticState.IN_PROGRESS);
      case "paid":
        return ordersList.filter((o) => o.payment_state === PaidState.PAID);
      case "pending":
        return ordersList.filter((o) => o.payment_state === PaidState.PENDING);
      case "ws_confirmed":
        return ordersList.filter((o) => confirmationStatuses[String(o.id)]?.status === "confirmed");
      case "ws_not_confirmed": {
        const notConfirmed = new Set(["rejected", "expired", "canceled", "send_failed"]);
        return ordersList.filter((o) => notConfirmed.has(confirmationStatuses[String(o.id)]?.status));
      }
      case "ws_awaiting": {
        const awaiting = new Set(["awaiting_response", "pending"]);
        return ordersList.filter((o) => awaiting.has(confirmationStatuses[String(o.id)]?.status));
      }
      case "ws_none":
        return ordersList.filter((o) => !confirmationStatuses[String(o.id)]);
      default:
        return ordersList;
    }
  };

  useEffect(() => {
    if (!orders?.orders) return;
    const filtered = applyFilter(orders.orders, activeFilter);
    setFilteredOrders({
      orders: filtered,
      summary: activeFilter === "all" ? orders.summary : calculateSummary(filtered),
    });
  }, [orders, activeFilter, confirmationStatuses]);

  const calculateSummary = (ordersList: Order[]) => {
    const total_value_with_discount_summary: number = ordersList.reduce(
      (summary, order) => {
        return summary + order.total_value_with_discount;
      },
      0
    );

    const total_value_summary: number = ordersList.reduce((summary, order) => {
      return summary + order.total_value;
    }, 0);

    const total_units_summary: number = ordersList.reduce((summary, order) => {
      return summary + order.total_units;
    }, 0);

    const total_buyers_summary = ordersList.filter(
      (order, index, self) =>
        index ===
        self.findIndex((t) => t.customer_email === order.customer_email)
    ).length;

    const response: Summary = {
      total_buyers: total_buyers_summary,
      total_units: total_units_summary.toFixed(0),
      total_value: total_value_summary.toFixed(2),
      total_value_with_discount: total_value_with_discount_summary.toFixed(2),
    };
    return response;
  };

  // TODO: ESTABLECER INTERFAZ PARA RESPUESTA DE API
  const normalizeOrders = (res: any): Orders => {
    return res.orders
      ? res
      : {
          orders: [],
          summary: {
            total_value: res.total_value?.toFixed(2) || "0.00",
            total_value_with_discount: res.total_value_with_discount?.toFixed(2) || "0.00",
            total_units: res.total_units?.toFixed(0) || "0",
            total_buyers: res.total_buyers || 0,
          },
          pagination: {
            page: 0,
            size: 15,
            total_items: 0,
            total_pages: 0,
          },
        };
  };

  const loadConfirmationStatuses = async (ordersList: Order[]) => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId || ordersList.length === 0) {
      setIsLoadingStatuses(false);
      return;
    }
    setIsLoadingStatuses(true);
    const orderIds = ordersList.map((o) => o.id);
    const { res, error } = await handleApi(getConfirmationStatuses(ownerId, orderIds), false);
    if (!error && res) {
      setConfirmationStatuses(res);
    }
    setIsLoadingStatuses(false);
  };

  const loadOrders = async (queryFilters: QueryFiltersParams) => {
    setIsLoading(true);
    try {
      const res = await getOrdersByUser(queryFilters);
      const data = normalizeOrders(res);
      setOrders(data);
      loadConfirmationStatuses(data.orders);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshOrders = () => {
    const filters = getQueryFiltersFromURL();
    loadOrders(filters);
  };

  const filterOrders = (filter: FilterOrdersType) => {
    if (!orders?.orders) return;
    setActiveFilter(filter);
    const filtered = applyFilter(orders.orders, filter);
    setFilteredOrders({
      orders: filtered,
      summary: filter === "all" ? orders.summary : calculateSummary(filtered),
    });
  };

  const filterByDate = async (start: Date, end: Date, label: string) => {
    const dateInit = formatDateToISOString(start);
    const dateEnd = formatDateToISOString(end);
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date_init', dateInit);
    newParams.set('date_end', dateEnd);
    newParams.set('filter_label', label);
    newParams.set('page', '0');
    setSearchParams(newParams);
    
    setActiveDateFilter({ label, startDate: start, endDate: end });
  };

  const filterByProductId = async (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const newParams = new URLSearchParams(searchParams);
    const currentProducts = newParams.get('product_ids')?.split(',').filter(Boolean) || [];
    
    if (!currentProducts.includes(productId)) {
      currentProducts.push(productId);
      newParams.set('product_ids', currentProducts.join(','));
      newParams.set('page', '0');
      
      const currentNames = newParams.get('product_names')?.split('||').filter(Boolean) || [];
      currentNames.push(product.name);
      newParams.set('product_names', currentNames.join('||'));
      
      setSearchParams(newParams);
      
      setActiveProductFilters([...activeProductFilters, { id: productId, name: product.name }]);
    }
  }

  const removeProductFilter = (productId: string) => {
    const newParams = new URLSearchParams(searchParams);
    const currentProducts = newParams.get('product_ids')?.split(',').filter(Boolean) || [];
    const currentNames = newParams.get('product_names')?.split('||').filter(Boolean) || [];
    
    const indexToRemove = currentProducts.indexOf(productId);
    if (indexToRemove > -1) {
      currentProducts.splice(indexToRemove, 1);
      currentNames.splice(indexToRemove, 1);
      
      if (currentProducts.length > 0) {
        newParams.set('product_ids', currentProducts.join(','));
        newParams.set('product_names', currentNames.join('||'));
      } else {
        newParams.delete('product_ids');
        newParams.delete('product_names');
      }
      newParams.set('page', '0');
      
      setSearchParams(newParams);
      
      setActiveProductFilters(activeProductFilters.filter(p => p.id !== productId));
    }
  }

  const clearDateFilter = async () => {
    setActiveDateFilter(null);
    
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('date_init');
    newParams.delete('date_end');
    newParams.delete('filter_label');
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const setPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(Math.max(0, page)));
    setSearchParams(newParams);
  };

  const setPageSize = (size: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('size', String(size));
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const deleteOrder = (orderId: string) => {
    if (!orders) return;
    const updated = orders.orders.filter((o) => o.id.toString() !== orderId);
    const newOrders = {
      ...orders,
      orders: updated,
      summary: calculateSummary(updated),
      pagination: orders.pagination
        ? {
            ...orders.pagination,
            total_items: Math.max(0, orders.pagination.total_items - 1),
          }
        : undefined,
    };
    setOrders(newOrders);
  };

  return {
    orders,
    filteredOrders,
    isLoading,
    activeFilter,
    activeDateFilter,
    activeProductFilters,
    confirmationStatuses,
    isLoadingStatuses,
    calculateSummary,
    filterOrders,
    filterByDate,
    clearDateFilter,
    deleteOrder,
    filterByProductId,
    removeProductFilter,
    products,
    pagination: orders?.pagination,
    setPage,
    setPageSize,
    refreshOrders,
  };
};
